import { getApiGatewayUrl } from './deviceStatusHandler';
import { v4 as uuidv4 } from 'uuid';
import { store } from '../app/store';
import { isMentatSerial } from '../constants/stationConfig';

const apigClientFactory = require('aws-api-gateway-client').default;

export enum EventName {
    // Installation
    DEVICE_INSTALLATION = 'DEVICE_INSTALLATION',
    DEVICE_COMPONENT_INSTALLATION = 'DEVICE_COMPONENT_INSTALLATION',
    REPORT_INSTALLATION_FAILURE = 'REPORT_INSTALLATION_FAILURE',
    CHECK_STATUS = 'CHECK_STATUS',
    MANUAL_VIN_MODIFICATION = 'MANUAL_VIN_MODIFICATION',
    MANUAL_DEVICE_SERIAL_NUMBER_MODIFICATION = 'MANUAL_DEVICE_SERIAL_NUMBER_MODIFICATION',
    FORCE_START_DEPLOYMENT = 'FORCE_START_DEPLOYMENT',
    REPAIR_DEVICE = 'REPAIR_DEVICE',
    REPAIR_DEVICE_STATE = 'REPAIR_DEVICE_STATE',
    REPAIR_ACTIONS_TAKEN = 'REPAIR_ACTIONS_TAKEN',
    NETRADYNE_DISSOCIATION = 'NETRADYNE_DISSOCIATION',

    // App Functionality
    VIN_DECODE_FROM_CAMERA = 'VIN_DECODE_FROM_CAMERA',
    SERIAL_NUMBER_DECODE_FROM_CAMERA = 'SERIAL_NUMBER_DECODE_FROM_CAMERA',
    LOGIN_ATTEMPT = 'LOGIN_ATTEMPT',

    // Drishti installation workflow
    DRISHTI_SITES_LISTED = 'DRISHTI_SITES_LISTED',
    DRISHTI_STATIONS_LISTED = 'DRISHTI_STATIONS_LISTED',
    DRISHTI_STATION_SELECTED = 'DRISHTI_STATION_SELECTED',
    DRISHTI_SERIAL_DECODE_FROM_CAMERA = 'DRISHTI_SERIAL_DECODE_FROM_CAMERA',
    DRISHTI_INSTALL_STARTED = 'DRISHTI_INSTALL_STARTED',
    DRISHTI_INSTALL_GUIDE_LOADED = 'DRISHTI_INSTALL_GUIDE_LOADED',
    DRISHTI_INSTALL_ENDED = 'DRISHTI_INSTALL_ENDED',
    DRISHTI_SUPPORT_REPORT_OPENED = 'DRISHTI_SUPPORT_REPORT_OPENED',

    // Drishti authentication. Prefixed: both apps write app_events with no column naming the app.
    DRISHTI_LOGIN_ATTEMPT = 'DRISHTI_LOGIN_ATTEMPT',
    DRISHTI_MANUAL_SIGN_OUT = 'DRISHTI_MANUAL_SIGN_OUT',
    DRISHTI_COGNITO_TOKEN_REFRESH = 'DRISHTI_COGNITO_TOKEN_REFRESH',

    // Drishti calibration lifecycle. Both phases share these names and carry `phase` in event_data.
    DRISHTI_CALIBRATION_STARTED = 'DRISHTI_CALIBRATION_STARTED',
    DRISHTI_CALIBRATION_STATUS_POLL = 'DRISHTI_CALIBRATION_STATUS_POLL',
    DRISHTI_CALIBRATION_OUTCOME_RECORDED = 'DRISHTI_CALIBRATION_OUTCOME_RECORDED',
    DRISHTI_CALIBRATION_COMPLETED = 'DRISHTI_CALIBRATION_COMPLETED',
    DRISHTI_CALIBRATION_FAILED = 'DRISHTI_CALIBRATION_FAILED',
    DRISHTI_CALIBRATION_ESCALATED = 'DRISHTI_CALIBRATION_ESCALATED',
    DRISHTI_CALIBRATION_ABANDONED = 'DRISHTI_CALIBRATION_ABANDONED',

    // Authentication
    PASSWORD_RESET = 'PASSWORD_RESET',
    MANUAL_SIGN_OUT = 'MANUAL_SIGN_OUT',
    COGNITO_TOKEN_REFRESH = 'COGNITO_TOKEN_REFRESH',
}

export enum EventType {
    START = 'START',
    SUCCESS = 'SUCCESS',
    FAILURE = 'FAILURE',
    DURATION = 'DURATION',
    COUNT = 'COUNT',
    FAILED_VALIDATION = 'FAILED_VALIDATION',
}

export type Event = {
    // device information
    readonly deviceId?: string;
    readonly vin?: string;

    // event data
    readonly eventName: EventName;
    readonly eventType: EventType;
    readonly componentType?: string;
    readonly componentId?: string;
    readonly duration_ms?: number;
    readonly miscEventData?: {};

    // user information
    readonly cookies?: {
        group: any;
        username: any;
        'custom:company': any;
        jwt: any;
    };
};

export const logMetricEvent = (event: Event) => {
    const client = apigClientFactory.newClient({
        invokeUrl: getApiGatewayUrl(),
    });
    let cookies;
    if (event.cookies) {
        console.log('No token present');
        cookies = event.cookies;
    } else {
        cookies = store.getState().userData.userInfo;
    }
    if (!cookies) {
        // /log_onboarding authorizes on the session's own jwt, so with no session there is nothing to send.
        console.warn(`Dropping ${event.eventName}: no signed-in session`);
        return;
    }
    client.invokeApi(
        {},
        '/log_onboarding',
        'POST',
        {
            queryParams: {
                // we will get userid and role from token in lambda
                clientEventId: uuidv4(),
                deviceId: event.deviceId || '',
                vin: event.vin || '',
                eventName: event.eventName,
                eventType: event.eventType,
                componentId: event.componentId,
                componentType: event.componentType,
                duration_ms: event.duration_ms ? event.duration_ms : 0,
                eventData: event.miscEventData ? JSON.stringify(event.miscEventData) : '',
                clientEventTime: Date.now(),
                userId: cookies.username,
                group: cookies.group,
                company: cookies['custom:company'],
            },
            headers: {
                Authorization: cookies.jwt,
            },
        },
        null,
    );
};

const EVENT_DATA_MAX_LENGTH = 1500;
// Per-value cap, so one long error body cannot crowd out the fields beside it.
const MAX_EVENT_DATA_VALUE_LENGTH = 300;
// The digits the event_duration_ms column holds; a real step never comes near it, so this only bounds the field.
const MAX_DURATION_MS = 999999999;
// What the handler's event_data pattern excludes ([^'\\]), plus the control characters
// JSON.stringify would escape with a backslash. Compared per character rather than matched with
// a regex, because a control-character range in a regex fails the build's no-control-regex rule.
const isSafeEventDataChar = (char: string) => char >= ' ' && char !== "'" && char !== '"' && char !== '\\';

const stripUnsafeChars = (value: string) => Array.from(value).filter(isSafeEventDataChar).join('');

// Applied to the values, not the finished JSON: stripping escapes afterwards leaves it unparseable.
const sanitizeEventData = (value: unknown): unknown => {
    if (typeof value === 'string') {
        return stripUnsafeChars(value).slice(0, MAX_EVENT_DATA_VALUE_LENGTH);
    }
    if (Array.isArray(value)) {
        return value.map(sanitizeEventData);
    }
    if (typeof value === 'object' && value !== null) {
        // Keys too: a response body spread into a payload contributes field names of its own.
        return Object.fromEntries(
            Object.entries(value).map(([key, entry]) => [stripUnsafeChars(key), sanitizeEventData(entry)]),
        );
    }
    return value;
};

// Quotes and backslashes are stripped because $util.escapeJavaScript would break the body the handler parses.
export const composeEventData = (payload?: {}): string => {
    if (!payload) {
        return '';
    }
    try {
        const composed = JSON.stringify(sanitizeEventData(payload));
        // Slicing mid-token would leave event_data unparseable, so it reports its size instead.
        return composed.length <= EVENT_DATA_MAX_LENGTH ? composed : `{"eventDataLength":${composed.length}}`;
    } catch {
        // Unserializable payload (a circular reference) — the event is worth more than its metadata.
        return '';
    }
};

// Sent blank rather than failing the whole log call when the serial has not been scanned yet.
const toDeviceSerial = (deviceId?: string): string => {
    const normalized = deviceId?.trim().toUpperCase() ?? '';
    return isMentatSerial(normalized) ? normalized : '';
};

// Blank, not 0, when the caller did not time the step: 0 would read as a step that finished instantly.
const toDurationMs = (durationMs?: number): string => {
    if (durationMs === undefined || !Number.isFinite(durationMs) || durationMs < 0) {
        return '';
    }
    return String(Math.min(Math.round(durationMs), MAX_DURATION_MS));
};

export const logDrishtiMetricEvent = (event: Event) => {
    const jwt = store.getState().drishtiUserData.session?.jwt;
    if (!jwt) {
        console.warn(`Dropping ${event.eventName}: no Drishti session`);
        return;
    }
    const client = apigClientFactory.newClient({ invokeUrl: getApiGatewayUrl(), retries: 0 });
    client
        .invokeApi(
            {},
            '/log_drishti_onboarding',
            'POST',
            {
                headers: {
                    Authorization: jwt,
                    'Content-Type': 'application/json',
                },
            },
            {
                // userId is stamped from the caller's token by the mapping template, not sent here.
                clientEventId: uuidv4(),
                eventName: event.eventName,
                eventType: event.eventType,
                clientEventTime: String(Date.now()),
                deviceSerial: toDeviceSerial(event.deviceId),
                duration_ms: toDurationMs(event.duration_ms),
                eventData: composeEventData(event.miscEventData),
            },
        )
        .catch((rejection: unknown) => {
            console.warn(`Failed to log ${event.eventName}`, rejection);
        });
};
